import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { REDUCER_KEY } from "constants/redux";

export interface iProjectCachedInfo {
  isFirstChecked: boolean;
  allUrlCachedCount: number;
  currentUrlCachedCount: number;
}

export enum ServiceWorkerStatus {
  UNINSTALLED = "UNINSTALLED",
  READY = "READY",
  INSTALLING = "INSTALLING",
  ERROR = "ERROR",
}

export enum AuthStatus {
  LOGGED = "LOGGED",
  LOGOUT = "LOGOUT",
  REFRESHING = "REFRESHING",
}

export interface AppState {
  size: {
    width: number;
    height: number;
  };
  isCollapsedLeftBar: boolean;
  isOpenMenu: boolean;
  loadingNetworkStatus: boolean;
  isShowOfflineMsg: boolean;
  serviceWorkerStatus: ServiceWorkerStatus;
  isSyncOfflineData: boolean;
  isCheckedPrecacheMissStaticFile: boolean;
  authStatus: AuthStatus;
  sizePanel: {
    width: number;
    unit: string;
  };
  isWebSocketBimFileOpen: boolean;
}

const initialState: AppState = {
  size: {
    width: 0,
    height: 0,
  },
  isCollapsedLeftBar: false,
  isOpenMenu: false,
  isShowOfflineMsg: true,
  isSyncOfflineData: false,
  loadingNetworkStatus: true,
  serviceWorkerStatus: ServiceWorkerStatus.UNINSTALLED,
  authStatus: AuthStatus.REFRESHING,
  isCheckedPrecacheMissStaticFile: false,
  sizePanel: {
    width: 0,
    unit: "px",
  },
  isWebSocketBimFileOpen: false,
};

export const appSlice = createSlice({
  name: REDUCER_KEY.APP,
  initialState,
  reducers: {
    setAppSize: (
      state,
      action: PayloadAction<{ width: number; height: number }>
    ) => {
      const { width } = action.payload;
      const widthRatio = Math.round(width / 3); // 3 is the ratio of the width of the panel
      const widthPanel = widthRatio >= 440 ? 440 : widthRatio; // 440 is the maximum width of the panel
      state.sizePanel = {
        width: widthPanel,
        unit: "px",
      };
      state.size = action.payload;
    },
    setIsSyncOfflineData: (state, action: PayloadAction<boolean>) => {
      state.isSyncOfflineData = action.payload;
    },
    setServiceWorkerStatus: (
      state,
      action: PayloadAction<ServiceWorkerStatus>
    ) => {
      state.serviceWorkerStatus = action.payload;
    },
    setIsCheckedPrecacheMissStaticFile: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.isCheckedPrecacheMissStaticFile = action.payload;
    },
    setAuthStatus: (state, action: PayloadAction<AuthStatus>) => {
      state.authStatus = action.payload;
    },
    setIsShowOfflineMsg: (state, action: PayloadAction<boolean>) => {
      state.isShowOfflineMsg = action.payload;
    },
    setLoadingNetworkStatus: (state, action: PayloadAction<boolean>) => {
      state.loadingNetworkStatus = action.payload;
    },
    setStatusWebSocketBimFileOpen: (state, action: PayloadAction<boolean>) => {
      state.isWebSocketBimFileOpen = action.payload;
    },
    setIsCollapsedLeftBar: (state, action: PayloadAction<boolean>) => {
      state.isCollapsedLeftBar = action.payload;
    },
    clearState: () => initialState,
  },
});

export const {
  setAppSize,
  setIsSyncOfflineData,
  setServiceWorkerStatus,
  setIsCheckedPrecacheMissStaticFile,
  setAuthStatus,
  clearState,
  setIsShowOfflineMsg,
  setStatusWebSocketBimFileOpen,
  setLoadingNetworkStatus,
  setIsCollapsedLeftBar,
} = appSlice.actions;

export default appSlice.reducer;
